import {
  allowedCSSProperties,
  sanitizeColorValue,
  sanitizeCssValue,
} from "@companion-core/shared/app/Utils/htmlSanitizer";
import { useEffect } from "react";

interface useFontProps {
  [fontFamily: string]: Array<{
    weight: number;
    url: string;
    format: string;
    style: string;
  }>;
}

export function useFonts(fonts: useFontProps) {
  useEffect(() => {
    if (!fonts) return;
    Object.keys(fonts).forEach((fontFamily: string) => {
      fonts[fontFamily].forEach((font) => {
        fetch(font.url)
          .then((response) => response.blob())
          .then((blob) => {
            const url = URL.createObjectURL(blob);

            const style = document.createElement("style");
            const styleSheet = style.sheet;
            if (!styleSheet) return;
            styleSheet.insertRule(`
              @font-face {
                font-family: '${sanitizeCssValue(fontFamily)}';
                font-style: '${sanitizeCssValue(font.style ?? "normal")}';
                font-weight: ${sanitizeCssValue(font.weight.toString())};
                src: url(${url}) format(${sanitizeCssValue(font.format)});
              }
            `);
            document.head.appendChild(style);
          });
      });
    });
  }, [fonts]);
}

interface useColorProps {
  colors?: { [key: string]: string };
}

export function useColors({ colors }: useColorProps) {
  useEffect(() => {
    if (!colors) return;
    for (const name in colors) {
      document.documentElement.style.setProperty(
        `--${sanitizeCssValue(name)}`,
        sanitizeColorValue(colors[name]),
      );
    }
  }, [colors]);
}

interface useStyleProps {
  styles?: Array<{
    selector: string;
    rules: React.CSSProperties;
  }>;
}

function formatStyle(selector: string, rules: React.CSSProperties): string {
  const rulesKeys = Object.keys(rules);
  const formattedRules = rulesKeys
    .map((key: string) => {
      if (!allowedCSSProperties.includes(key)) return;
      return `\t${key}: ${rules[key as keyof typeof rules]};`;
    })
    .join("\n");
  return `
${selector} {\n${formattedRules}\n}
  `;
}

export function useStyle({ styles }: useStyleProps) {
  useEffect(() => {
    if (!styles) return;
    const styleElement = document.createElement("style");
    const styleSheet = styleElement.sheet;
    if (styleSheet) {
      styles.forEach((style) => {
        styleSheet.insertRule(formatStyle(style.selector, style.rules));
      });
    } else {
      let mergedStyles = "";
      styles.forEach((style) => {
        mergedStyles += formatStyle(style.selector, style.rules);
      });
      styleElement.appendChild(document.createTextNode(mergedStyles));
    }
    document.head.appendChild(styleElement);
  }, []);
}

interface useFaviconProps {
  assets?: { [key: string]: string };
}

export function useFavicon({ assets }: useFaviconProps) {
  useEffect(() => {
    if (!assets) return;
    const { favicon } = assets;
    const link: HTMLLinkElement = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    link.href = favicon;
  }, []);
}
